import React, { useContext, useState, useEffect, useRef } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ProjectContext } from '../../../context/ProjectContext'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import { Star, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Checkbox } from "@/components/ui/checkbox"
import { SelectBar } from '../selectBar'
import { createPortal } from 'react-dom'
import { DeleteNotesDialog } from './deleteNotesDialog'

const NotesTable = ({ notes }) => {
  const { 
    categories, 
    meetings, 
    participants,
    selectedNotes,
    setSelectedNotes,
    firstNoteSelected,
    setFirstNoteSelected,
    noteHover,
    setNoteHover,
    showDeleteNotesDialog,
    setShowDeleteNotesDialog,
    handleDeleteNotes,
    setSelectedFeedbacks,
    setNotes
  } = useContext(ProjectContext)
  const customFetch = useCustomFetch()
  const [expandedCategories, setExpandedCategories] = useState([])
  const selectBarRef = useRef(null)

  // Initialize expandedCategories with all category IDs
  useEffect(() => {
    const categoryIds = notes.map(note => note.category_id || 'uncategorized')
    const uniqueCategoryIds = [...new Set(categoryIds)]
    setExpandedCategories(uniqueCategoryIds)
  }, [notes])

  const getMeetingName = (meetingId) => {
    if (!meetingId) return 'General Notes'
    const meeting = meetings.find(m => m.id === meetingId)
    return meeting ? meeting.name : 'Unknown Meeting'
  }

  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Uncategorized'
    const category = categories.find(c => c.id === categoryId)
    return category ? category.content : 'Unknown Category'
  }

  const getParticipantName = (participantId) => {
    if (!participantId) return '-'
    return participants[participantId]?.name || '-'
  }

  const handleStarNote = async (noteId, currentStar) => {
    try {
      await customFetch('/updateNoteStar', 'PUT', { 
        notes: [noteId], 
        star: currentStar ? null : 'starred' 
      })
      // Update the local notes state
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId 
            ? { ...note, star: currentStar ? null : 'starred' }
            : note
        )
      )
    } catch (error) {
      console.error('Error updating star status:', error)
    }
  }

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const handleNoteSelect = (noteId, event) => {
    event.stopPropagation()
    setSelectedFeedbacks([])
    
    if (event.ctrlKey || event.metaKey) {
      // Ctrl (or Cmd) key for multiple select
      setSelectedNotes(prev => 
        prev.includes(noteId)
          ? prev.filter(id => id !== noteId)
          : [...prev, noteId]
      )
      if (selectedNotes.length === 0) {
        setFirstNoteSelected(noteId)
        setNoteHover(noteId)
      }
    } else if (event.shiftKey) {
      // Shift key for range select
      if (selectedNotes.length === 0) {
        setSelectedNotes([noteId])
        setFirstNoteSelected(noteId)
        setNoteHover(noteId)
        return
      }
      const currentNoteIndex = notes.findIndex(note => note.id === noteId)
      const firstNoteIndex = notes.findIndex(note => note.id === firstNoteSelected)

      if (currentNoteIndex < firstNoteIndex) {
        setSelectedNotes(notes.slice(currentNoteIndex, firstNoteIndex + 1).map(note => note.id))
      } else if (currentNoteIndex > firstNoteIndex) {
        setSelectedNotes(notes.slice(firstNoteIndex, currentNoteIndex + 1).map(note => note.id))
      }
    } else {
      // Single select
      setSelectedNotes([noteId])
      setFirstNoteSelected(noteId)
      setNoteHover(noteId)
    }
  }

  // Group notes by category
  const groupedNotes = notes.reduce((acc, note) => {
    const categoryId = note.category_id || 'uncategorized'
    if (!acc[categoryId]) {
      acc[categoryId] = []
    }
    acc[categoryId].push(note)
    return acc
  }, {})

  return (
    <div className="w-full space-y-4">
      {Object.entries(groupedNotes).map(([categoryId, categoryNotes]) => {
        const categoryName = getCategoryName(categoryId)
        const isExpanded = expandedCategories.includes(categoryId)

        return (
          <div key={categoryId} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleCategory(categoryId)}
              className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              <div className="flex items-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{categoryName}</div>
                <div className="ml-2 text-sm text-gray-500 dark:text-gray-400">({categoryNotes.length})</div>
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              )}
            </button>

            {isExpanded && (
              <div className="p-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50%]">Note</TableHead>
                      <TableHead className="w-[10%] text-center">Cluster</TableHead>
                      <TableHead className="w-[15%]">Meeting</TableHead>
                      <TableHead className="w-[10%]">Created</TableHead>
                      <TableHead className="w-[10%]">Participant</TableHead>
                      <TableHead className="w-[5%] text-center">Star</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryNotes.map((note) => (
                      <TableRow 
                        key={note.id}
                        className={cn(
                          "cursor-pointer",
                          selectedNotes.includes(note.id) && "bg-gray-50 dark:bg-gray-700"
                        )}
                        onClick={(event) => handleNoteSelect(note.id, event)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              className="h-4 w-4"
                              checked={selectedNotes.includes(note.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedNotes(prev => [...prev, note.id])
                                  setFirstNoteSelected(note.id)
                                  setNoteHover(note.id)
                                } else {
                                  setSelectedNotes(prev => prev.filter(id => id !== note.id))
                                  if (firstNoteSelected === note.id) {
                                    setFirstNoteSelected(null)
                                    setNoteHover(null)
                                  }
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {note.color && (
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: note.color }}
                              />
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
                                    {note.content}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-[400px] break-words">{note.content}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {Array.isArray(note.clustered_notes) && note.clustered_notes.length > 0 && note.meetings_count > 1 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-center">
                                    <div className="relative w-6 h-6">
                                      <svg 
                                        width="24" 
                                        height="24" 
                                        viewBox="0 0 24 24" 
                                        className="absolute top-0 left-0"
                                      >
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          fill="black"
                                          stroke="#333333"
                                          strokeWidth="2"
                                        />
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          fill="none"
                                          stroke="#FF4A8E"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                          strokeDasharray={`${(Math.round((note.meetings_count * 100) / meetings.length) / 100) * 62.83} 62.83`}
                                          transform="rotate(-90 12 12)"
                                        />
                                      </svg>
                                      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold">
                                        {note.meetings_count}
                                      </span>
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Appears in {note.meetings_count} out of {meetings.length} meetings ({Math.round((note.meetings_count * 100) / meetings.length)}%)</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                          {getMeetingName(note.meetingId)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(note.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                          {getParticipantName(note.participant_id)}
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleStarNote(note.id, note.star)
                            }}
                            className={cn(
                              "p-1 rounded-full hover:bg-gray-100",
                              note.star && "text-yellow-500"
                            )}
                          >
                            <Star 
                              className="w-4 h-4" 
                              fill={note.star ? "currentColor" : "none"}
                            />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )
      })}

      {/* {selectedNotes.length > 0 && noteHover && (
        <SelectBar note={notes.find(n => n.id === noteHover)} noteOrFeedback="note" ref={selectBarRef} />
      )} */}
    </div>
  )
}

export default NotesTable 